import { fetchUtils } from '$utils/fetchUtils'

export type UserPreferenceValue = Record<string, unknown>

/**
 * @example
 getUserPreference('news-badges')
 setUserPreference('news-badges', {'ai-analysis':
 {
 'clicked': true,
 'seen': 3,
 }
 })
 */

// TODO: error handling? timeouts?
// TODO: in Svelte 5, use the async fetch from the map work which also includes abortSignal handling
export async function getUserPreference<T extends UserPreferenceValue = UserPreferenceValue>(
	preferenceKey: string
): Promise<T | null> {
	try {
		/**
		 * publishPageError: false — a UI preference is not an authorization boundary,
		 * and MetaBroker reads this on every route, so letting doHandle own the failure
		 * sends anonymous visitors on public pages to /login.
		 *
		 * the try/catch is the only handler here: fetchUtils.catchErrorAndShowNotification
		 * is a factory, so an inline .catch(that) resolves the promise with the handler
		 * function itself and callers store it as a preference.
		 */
		const response = await fetchUtils.get(`/api/preference/get/${preferenceKey}`, false)

		// doHandle returns `json.data || json`.
		// - When a preference is stored: json.data is a JSON string → parse it.
		// - When nothing is stored: json.data is null, so the bare success envelope
		//   ({ success, errorCode, ... }) is returned. Detect and discard it so it
		//   never leaks into callers or back into the database.
		if (typeof response === 'string') {
			return JSON.parse(response) as T
		}
		if (response != null && !('success' in response)) {
			return response as T
		}

		// console.warn(`No user preference for ${preferenceKey}`)
	} catch (error) {
		// backend drift — e.g. no user preferences table or no entry for user
		console.warn(`Failed to fetch user preference for ${preferenceKey}`, error)
	}

	return null
}

// TODO: consider making async so that we can block
// TODO: error handling? timeouts?
// TODO: in Svelte 5, use the async fetch from the map work which also includes abortSignal handling
export function setUserPreference<T extends UserPreferenceValue = UserPreferenceValue>(
	preferenceKey: string,
	value: T
) {
	/**
	 * publishPageError: false, and the failure is swallowed rather than toasted:
	 * MetaBroker seeds this key on every page load whose read came back empty, so on
	 * a public page it fails for every anonymous visitor. Neither a navigation nor a
	 * "Missing permission" toast belongs on a page they just opened; doHandle still
	 * logs the rejection for developers.
	 */
	// the object needs to be wrapped in an object with {value: ...} for sirius to get the correct data
	return fetchUtils.post(`/api/preference/put/${preferenceKey}`, { value }, false).catch(() => {})
}
